import { Composite, Engine, Events, Runner, World } from 'matter-js';
import { AbstractEntity } from '../entities/AbstractEntity';
import { BubbleEntity } from '../entities/BubbleEntity';
import { ClusterEntity } from '../entities/ClusterEntity';
import { WallEntity } from '../entities/WallEntity';
import { applySoftBounds } from '../physics/forces';

export class SimulationEngine {
    public _engine: Engine;                 // Expone motor para casos avanzados (colisiones, etc)
    private runner: Runner;                 // Controla el loop de tiempo
    private entities: Map<string, AbstractEntity> = new Map(); // Almacena las entidades activas en el campo

    // Referencia al contenedor DOM
    private container: HTMLElement | null = null;
    private width: number = window.innerWidth;      // Dimensiones del campo, se actualizan con resize
    private height: number = window.innerHeight;    // Dimensiones del campo, se actualizan con resize
    private isRunning: boolean = false;             // Estado del motor (corriendo o detenido)
    private readonly onWindowResize = () => this._UpdateDimensions(); // Referencia estable para poder remover el listener

    public _AfterUpdateCallback: () => void = null;

    constructor() {
        // 1. Crear el motor físico
        this._engine = Engine.create({
            gravity: { x: 0, y: 0, scale: 0 }, // sin gravedad
        });

        // 2. Crear el corredor (runner) que maneja el loop de tiempo
        this.runner = Runner.create();
    }

    // *************************************************************************
    //#region API

    public _Init(container: HTMLElement) {
        this.container = container;
        this._UpdateDimensions();

        window.addEventListener('resize', this.onWindowResize);

        // ** EL BUCLE PRINCIPAL **
        // Engancha al evento 'afterUpdate' de Matter.js
        // asegura que actualiza el DOM justo despues de calcular físicas
        Events.on(this._engine, 'afterUpdate', () => {
            this.loop();
            if (this._AfterUpdateCallback) {
                this._AfterUpdateCallback();
            }
        });
    }

    public _HasEntity(id: string): boolean {
        return this.entities.has(id);
    }

    public _GetEntityById(id: string): AbstractEntity | undefined {
        return this.entities.get(id);
    }

    public _GetEntities(): AbstractEntity[] {
        return Array.from(this.entities.values());
    }

    public _GetTotalEntities(...filters: (typeof BubbleEntity | typeof ClusterEntity | typeof WallEntity)[]): number {
        if (!filters || filters.length === 0) {
            return this.entities.size;
        }
        return Array.from(this.entities.values())
            .filter(entity => filters.some(filter => entity instanceof filter)).length;
    }

    /**
     * Agrega una entidad al sistema:
     * 1. A la simulación física (World)
     * 2. Al registro interno (Map)
     * 3. Al DOM (HTML)
     */
    public _AddEntity(entity: AbstractEntity) {
        if (!this.container) throw new Error("SimulationEngine not initialized with container");

        // 1. Registro
        this.entities.set(entity._id, entity);

        // 2. Física (Agrega el cuerpo principal)
        World.add(this._engine.world, entity._body);

        // 3. DOM (Inyecta el elemento visual)
        this.container.appendChild(entity._element);
    }

    /**
     * Metodo auxiliar para agregar cosas extra que no son entidades completas
     * (por ejemplo: los resortes/constraints del cluster)
     */
    public _AddPhysicsObject(object: any) {
        World.add(this._engine.world, object);
    }

    /**
     * Elimina una entidad del sistema:
     * 1. De la simulación física (World)
     * 2. Del DOM (HTML)
     * 3. Del registro interno (Map)
     */
    public _RemoveEntity(entityId: string) {
        const entity = this.entities.get(entityId);
        if (entity) {
            // 1. Física
            World.remove(this._engine.world, entity._body);
            // 2. DOM
            if (entity._element.parentNode) {
                entity._element.parentNode.removeChild(entity._element);
            }
            // 3. Limpieza interna
            entity._Destroy();
            this.entities.delete(entityId);
        }
    }

    public _RemovePhysicsObject(object: any) {
        World.remove(this._engine.world, object);
    }

    public _Start() {
        if (!this.isRunning) {
            Runner.run(this.runner, this._engine);
            this.isRunning = true;
        }
    }

    public _Stop() {
        Runner.stop(this.runner);
        this.isRunning = false;
        window.removeEventListener('resize', this.onWindowResize); // Evitar fuga de listeners si el componente se recrea
    }

    /** Refresca las dimensiones del contenedor */
    public _UpdateDimensions() {
        if (this.container) {
            this.width = this.container.clientWidth;
            this.height = this.container.clientHeight;
        }
    }

    //#endregion
    // *****************************************************************

    // *****************************************************************
    //#region Lógica

    // Se ejecuta ~60 veces por segundo
    private loop() {
        const allBodies = Composite.allBodies(this._engine.world);

        const ignoreCategories = [
            // COLLISION_CATEGORIES.INNER_BUBBLE,
            // COLLISION_CATEGORIES.CLUSTER,
        ];
        // Aplica fuerzas globales (Soft Bounds)
        applySoftBounds(allBodies, this.width, this.height, ignoreCategories);

        // Limitador de velocidad (clamp velocity): NA

        // Sincroniza DOM de cada entidad
        this.entities.forEach((entity) => {
            entity._Sync();
        });
    }

    // #endregion
    // *****************************************************************
}